import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../../../../../services/api";
import { useForm, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import cloneDeep from "lodash/cloneDeep";
import * as Dialog from "@radix-ui/react-dialog";
import SyncLoader from "react-spinners/SyncLoader";
import { format } from "date-fns";
import { useAuth } from "../../../../../../hooks/auth";
import { debounce } from "lodash";
import { useRoutes } from "../../../../../../hooks/routes";

import IHookForm from "../../../../../../shared/dtos/IHookForm";
import IListClauseApiProps from "./dtos/IListClauseApiProps";
import IListClauseDataParams from "./dtos/IListClauseDataParams";
import IFormSearch from "./dtos/IFormSearch";

import MasterAsideControls from "../../../../components/SidebarMaster/MasterAsideControls";
import HeaderMaster from "../../../../components/SidebarMaster/HeaderMaster";
import MasterContainer from "../../../../components/SidebarMaster/MasterContainer";
import MasterMain from "../../../../components/SidebarMaster/MasterMain";

import DialogModal from "../../../../../../shared/components/DialogModal";
import Button from "../../../../../../shared/components/Button";
import Input from "../../../../../../shared/components/Input";
import IconSvg from "../../../../../../shared/components/IconSvg";
import Pagination from "../../../../../../shared/components/Pagination";
import Tooltip from "../../../../../../shared/components/Tooltip";

import penIcon from "../../../../../../shared/assets/pen-icon.svg";
import trashIcon from "../../../../../../shared/assets/trash-icon.svg";
import iconSearchWhite from "../../../../../../shared/assets/icon-search-white.svg";

import { Notify } from "../../../../../../shared/utils";

import {
  BoxList,
  ButtonFlexBox,
  ButtonSearch,
  FlexBox,
  HStack,
  Main,
  Form,
  ContainerCreate,
  FlexPagination,
  ContainerLoading,
  Table,
  TableTh,
  TableTr,
  TableTd,
  TableThead,
  TableTbody,
  TableTdText,
} from "./styles";

const ListClause: React.FC = () => {
  const location = useLocation();
  const dataLocation: any = location.state;

  const { getUrl } = useRoutes();

  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [dataClauseList, setDataClauseList] = useState<IListClauseApiProps>({
    pagination: 0,
    dataArray: [],
  });
  const [listClauseDataParams, setListClauseDataParams] =
    useState<IListClauseDataParams>({
      pagination:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.pagination
          : 0,
      filter:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.filter
          : "all",
      is_dash:
        dataLocation && dataLocation.params != null
          ? dataLocation.params.is_dash
          : true,
    });

  const { data: dataUseAuth } = useAuth();

  const userPermissionCreate =
    dataUseAuth.admin_user_permissions.create_clauses;
  const userPermissionRead = dataUseAuth.admin_user_permissions.read_clauses;
  const userPermissionUpdate =
    dataUseAuth.admin_user_permissions.update_clauses;
  const userPermissionDelete =
    dataUseAuth.admin_user_permissions.delete_clauses;

  const navigate = useNavigate();

  useEffect(() => {
    getUrl("/clauses");
  }, []);

  const handleCreateClause = useCallback(() => {
    navigate("/create-clause", {
      state: { params: listClauseDataParams },
    });
  }, [navigate, listClauseDataParams]);

  useEffect(() => {
    (async () => {
      const response = await api.get("/bits-clause", {
        params: listClauseDataParams,
      });

      setDataClauseList(response.data);
      setTotalPages(response.data.pagination);
      setLoading(false);
    })();
  }, [reload, listClauseDataParams]);

  const handleDeleteClause = useCallback(
    async (id: string) => {
      try {
        await api.delete(`/bits-clause/${id}`);
        setReload(!reload);
      } catch (error: any) {
        console.log(error);
      }
    },
    [reload]
  );

  const formSchema = yup.object().shape({
    search: yup.string(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IHookForm>({
    mode: "onChange",
    resolver: yupResolver(formSchema),
  });

  const onSubmit = useCallback(
    debounce((formData: string) => {
      try {
        setLoading(true);
        if (formData === "") {
          setListClauseDataParams({
            pagination: 0,
            filter: "all",
            is_dash: true,
          });
        } else {
          setListClauseDataParams((state) => {
            let cloneState = cloneDeep(state);

            return {
              pagination: 0,
              filter: cloneState.filter,
              search: formData,
              is_dash: true,
            };
          });
        }
      } catch (err: any) {
        console.log(err?.response?.data);
      }
    }, 400),
    []
  );

  const navigateView = useCallback(
    (item) => {
      navigate(`/view-clause/${item.id}`, {
        state: { params: listClauseDataParams },
      });
    },
    [navigate, listClauseDataParams]
  );

  const notifyRead = useCallback(() => {
    Notify({ text: "Você não tem permissão de visualizar", type: "error" });
  }, []);

  const handleInputSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const searchTerm = event.target.value;

      onSubmit(searchTerm);
    },
    [onSubmit]
  );

  return (
    <MasterContainer>
      <MasterAsideControls />
      <HeaderMaster namePage="Lista de cláusulas" />
      <MasterMain>
        <Main>
          <ContainerCreate>
            <Button
              onClick={handleCreateClause}
              disabled={!userPermissionCreate}
            >
              Criar cláusula
            </Button>
          </ContainerCreate>
          <HStack>
            <Input
              onChangeInput={handleInputSearchChange}
              label="search"
              register={register}
              required={true}
              placeholder="Pesquisar"
              style={{ width: "100%" }}
            />
          </HStack>

          <BoxList>
            {loading && (
              <ContainerLoading>
                <SyncLoader loading={loading} color="#663399" size={10} />
              </ContainerLoading>
            )}

            {!loading && (
              <Table>
                <TableThead>
                  <TableTr>
                    <TableTh>Cláusulas</TableTh>
                    <TableTh>Data de criação</TableTh>
                    <TableTh>Última modificação</TableTh>
                    <TableTh>Opções</TableTh>
                  </TableTr>
                </TableThead>

                <TableTbody>
                  {dataClauseList.dataArray.map((item, index) => (
                    <TableTr key={item.id}>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <Tooltip
                          title={item.clause}
                          placement="top"
                          maxWidth={600}
                        >
                          <TableTdText>{item.clause}</TableTdText>
                        </Tooltip>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.created_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>
                      <TableTd
                        onClick={() =>
                          userPermissionRead ? navigateView(item) : notifyRead()
                        }
                      >
                        <TableTdText>
                          {format(
                            new Date(item.updated_at),
                            "dd/MM/yyyy HH:mm"
                          )}
                        </TableTdText>
                      </TableTd>

                      <TableTd>
                        <FlexBox>
                          <Tooltip title="Editar cláusula" placement="top">
                            <ButtonFlexBox
                              onClick={() => {
                                navigate(`/edit-clause/${item.id}`, {
                                  state: { params: listClauseDataParams },
                                });
                              }}
                              disabled={!userPermissionUpdate}
                            >
                              <IconSvg width="18" height="18" image={penIcon} />
                            </ButtonFlexBox>
                          </Tooltip>

                          <Dialog.Root>
                            <Tooltip title="Deletar cláusula" placement="top">
                              <Dialog.Trigger asChild>
                                <ButtonFlexBox disabled={!userPermissionDelete}>
                                  <IconSvg
                                    width="18"
                                    height="18"
                                    image={trashIcon}
                                  />
                                </ButtonFlexBox>
                              </Dialog.Trigger>
                            </Tooltip>

                            <DialogModal
                              title="Deletar cláusula"
                              subtitle={item.clause}
                              type="delete"
                              callback={() => handleDeleteClause(item.id)}
                            />
                          </Dialog.Root>
                        </FlexBox>
                      </TableTd>
                    </TableTr>
                  ))}
                </TableTbody>
              </Table>
            )}
          </BoxList>
          <FlexPagination>
            {dataClauseList.dataArray.length > 0 && (
              <Pagination
                total={totalPages}
                offset={listClauseDataParams.pagination}
                setDataParams={setListClauseDataParams}
                dataParams={listClauseDataParams}
              />
            )}
          </FlexPagination>
        </Main>
      </MasterMain>
    </MasterContainer>
  );
};

export default ListClause;
